import { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import { toast } from "sonner";
import { FileDown, FileText, Calendar, Hash } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [generating, setGenerating] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [rpt, cust] = await Promise.all([
          axios.get(`${API}/pdf/reports`, { withCredentials: true }),
          axios.get(`${API}/customers`, { withCredentials: true }),
        ]);
        setReports(rpt.data.items || []);
        setCustomers(cust.data.items || cust.data || []);
      } catch {
        toast.error("Error cargando datos");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const generateKYC = async (customerId) => {
    setGenerating(customerId);
    try {
      const res = await axios.post(`${API}/pdf/kyc/${customerId}`, {}, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = res.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, "") || "KYC_Report.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Reporte KYC generado");
      // Refresh reports list
      const rpt = await axios.get(`${API}/pdf/reports`, { withCredentials: true });
      setReports(rpt.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando reporte");
    } finally {
      setGenerating(null);
    }
  };

  return (
    <Layout>
      <div data-testid="reports-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Reportes PDF</h1>
            <p className="text-slate-400 mt-1">Generacion de reportes de cumplimiento</p>
          </div>
          <Button onClick={() => setShowAuditDialog(true)} data-testid="generate-audit-btn">
            <FileDown size={16} className="mr-2" /> Reporte de Auditoria
          </Button>
        </div>

        {/* KYC Report Generator */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl mb-6" data-testid="kyc-generator">
          <div className="px-5 py-4 border-b border-slate-800">
            <h2 className="text-lg font-semibold text-white">Generar Reporte KYC</h2>
            <p className="text-xs text-slate-500 mt-1">Selecciona un cliente para generar su reporte KYC consolidado</p>
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : customers.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No hay clientes registrados</div>
          ) : (
            <div className="divide-y divide-slate-800">
              {customers.slice(0, 20).map((c) => {
                const name = c.legal_name || `${c.first_name || ""} ${c.paternal_surname || ""}`.trim();
                return (
                  <div key={c.customer_id} className="px-5 py-3 flex items-center justify-between" data-testid={`kyc-row-${c.customer_id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center">
                        <FileText size={14} className="text-teal-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{name || "Sin nombre"}</p>
                        <p className="text-xs text-slate-500">{c.customer_number} | {c.person_type === "moral" ? "Persona Moral" : "Persona Fisica"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={
                        c.kyc_status === "verified" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" :
                        "bg-amber-500/10 text-amber-400 border-amber-500/20"
                      }>
                        {c.kyc_status}
                      </Badge>
                      <Button size="sm" onClick={() => generateKYC(c.customer_id)}
                        disabled={generating === c.customer_id}
                        data-testid={`kyc-gen-${c.customer_id}`}>
                        <FileDown size={14} className="mr-1" />
                        {generating === c.customer_id ? "Generando..." : "PDF"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Previous Reports */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl" data-testid="reports-history">
          <div className="px-5 py-4 border-b border-slate-800">
            <h2 className="text-lg font-semibold text-white">Reportes Generados</h2>
          </div>
          {reports.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No hay reportes generados</div>
          ) : (
            <div className="divide-y divide-slate-800">
              {reports.map((r) => (
                <div key={r.report_id} className="px-5 py-3 flex items-center justify-between" data-testid={`report-${r.report_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
                      <FileText size={14} className="text-violet-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{r.filename}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-2">
                        <Hash size={10} /> {r.report_id}
                        <Calendar size={10} className="ml-1" /> {new Date(r.created_at).toLocaleDateString("es-MX")}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-slate-700/50 text-slate-300">{r.report_type?.toUpperCase()}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>

        <AuditReportDialog open={showAuditDialog} onClose={() => setShowAuditDialog(false)} onGenerated={async () => {
          const rpt = await axios.get(`${API}/pdf/reports`, { withCredentials: true });
          setReports(rpt.data.items || []);
        }} />
      </div>
    </Layout>
  );
}

function AuditReportDialog({ open, onClose, onGenerated }) {
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState("2024-01-01");
  const [dateTo, setDateTo] = useState(today);
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/pdf/audit`, { date_from: dateFrom, date_to: dateTo }, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Audit_${dateFrom}_${dateTo}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Reporte de auditoria generado");
      onClose();
      onGenerated();
    } catch (e) {
      toast.error("Error generando reporte de auditoria");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white" data-testid="audit-report-dialog">
        <DialogHeader>
          <DialogTitle>Generar Reporte de Auditoria</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Desde</Label>
            <Input type="date" className="bg-slate-800 border-slate-700" value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)} data-testid="audit-date-from" />
          </div>
          <div>
            <Label>Hasta</Label>
            <Input type="date" className="bg-slate-800 border-slate-700" value={dateTo}
              onChange={(e) => setDateTo(e.target.value)} data-testid="audit-date-to" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleGenerate} disabled={generating} data-testid="audit-generate-btn">
            {generating ? "Generando..." : "Generar PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
